import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

import { ReactComponent as CopyIcon } from 'assets/icons/copy.svg';
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg';
import { useCopyToClipboard } from 'hooks/useCopyToClipboard';
import { cn } from 'lib/utils';

export interface CopyableFieldProps {
  /**
   * The label displayed before the value (e.g., "Authorization:")
   */
  label?: string;
  /**
   * The value displayed in the field
   */
  value: string;
  /**
   * Optional icon component to display before the label
   */
  icon?: React.ReactNode;
  /**
   * Optional callback function called when copy is triggered (for analytics, etc.)
   */
  onCopy?: () => void;
  /**
   * Optional className for additional styling
   */
  className?: string;
  /**
   * Optional title/tooltip for the copy button
   */
  copyButtonTitle?: string;
  /**
   * When provided, the field renders `mask(value)` by default and adds a
   * visibility toggle. The copy button always copies the unredacted `value`.
   */
  mask?: (value: string) => string;
}

// Sized to match Input + Button (h-10) so the row reads as a unified
// control band when stacked side-by-side. Tokens line up with the rest
// of the form primitives (line-strong border, input-bg, sharp corners).
export const CopyableField = ({
  label,
  value,
  icon,
  onCopy,
  className,
  copyButtonTitle = 'Copy to clipboard',
  mask,
}: CopyableFieldProps) => {
  const { copy, didCopy } = useCopyToClipboard();
  const [revealed, setRevealed] = useState(false);

  const handleCopy = () => {
    copy(value);
    onCopy?.();
  };

  // Only enable the toggle if the masker actually changes the value —
  // otherwise we'd show a Show/Hide control over text that has nothing to
  // hide (e.g. an empty key, or a value the masker doesn't recognise).
  const maskedValue = mask?.(value);
  const isMaskable = maskedValue !== undefined && maskedValue !== value;
  const displayed = isMaskable && !revealed ? maskedValue : value;
  const toggleTitle = revealed ? 'Hide value' : 'Show value';

  return (
    <div
      className={cn(
        'flex h-10 w-full items-center gap-2 border border-[var(--line-strong)] bg-[var(--input-bg)] px-3.5 text-sm text-[var(--text-muted)]',
        className,
      )}
    >
      {icon}
      {label && <h4 className="text-sm font-bold text-[var(--text)]">{label}</h4>}
      <code className="truncate font-mono text-sm text-[var(--text-muted)]">{displayed}</code>
      {isMaskable && (
        <button
          type="button"
          title={toggleTitle}
          aria-label={toggleTitle}
          aria-pressed={revealed}
          className="ml-auto inline-flex h-6 w-6 shrink-0 items-center justify-center text-[var(--text-muted)] transition-colors hover:text-[var(--text)] [&>svg]:size-4"
          onClick={() => setRevealed((r) => !r)}
        >
          {revealed ? <EyeOff /> : <Eye />}
        </button>
      )}
      <button
        type="button"
        title={copyButtonTitle}
        aria-label={copyButtonTitle}
        className={cn(
          'inline-flex h-6 w-6 shrink-0 items-center justify-center text-[var(--text-muted)] transition-colors hover:text-[var(--text)] [&>svg]:size-4 [&>svg]:fill-current',
          !isMaskable && 'ml-auto',
        )}
        onClick={handleCopy}
      >
        {didCopy ? <CheckIcon /> : <CopyIcon />}
      </button>
    </div>
  );
};
